import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth'
import type { User as FirebaseUser } from 'firebase/auth'
import { ref, computed } from 'vue'

export interface FirebaseAuthUser {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  emailVerified: boolean
  phoneNumber: string | null
}

export const useFirebaseAuth = () => {
  const { $firebaseAuth } = useNuxtApp()
  
  // Reactive state
  const currentUser = ref<FirebaseAuthUser | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Computed properties
  const isAuthenticated = computed(() => !!currentUser.value)
  const isEmailVerified = computed(() => currentUser.value?.emailVerified ?? false)

  /**
   * Sign in with email and password
   */
  const signIn = async (email: string, password: string): Promise<FirebaseAuthUser | null> => {
    isLoading.value = true
    error.value = null

    try {
      const userCredential = await signInWithEmailAndPassword($firebaseAuth, email, password)
      const user = userCredential.user
      
      const authUser: FirebaseAuthUser = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        phoneNumber: user.phoneNumber,
      }

      currentUser.value = authUser
      return authUser
    } catch (err: any) {
      error.value = getFirebaseErrorMessage(err.code)
      throw new Error(error.value)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Create a new user account
   */
  const signUp = async (email: string, password: string, displayName?: string): Promise<FirebaseAuthUser | null> => {
    isLoading.value = true
    error.value = null

    try {
      const userCredential = await createUserWithEmailAndPassword($firebaseAuth, email, password)
      const user = userCredential.user

      // Update display name if provided
      if (displayName) {
        await updateProfile(user, { displayName })
      }

      const authUser: FirebaseAuthUser = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        phoneNumber: user.phoneNumber,
      }

      currentUser.value = authUser
      return authUser
    } catch (err: any) {
      error.value = getFirebaseErrorMessage(err.code)
      throw new Error(error.value)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Sign out the current user
   */
  const signOutUser = async (): Promise<void> => {
    isLoading.value = true
    error.value = null

    try {
      await signOut($firebaseAuth)
      currentUser.value = null
    } catch (err: any) {
      error.value = getFirebaseErrorMessage(err.code)
      throw new Error(error.value)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Send password reset email
   */
  const resetPassword = async (email: string): Promise<void> => {
    isLoading.value = true
    error.value = null

    try {
      await sendPasswordResetEmail($firebaseAuth, email)
    } catch (err: any) {
      error.value = getFirebaseErrorMessage(err.code)
      throw new Error(error.value)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get the current user's ID token
   */
  const getIdToken = async (forceRefresh = false): Promise<string | null> => {
    if (!currentUser.value) return null

    try {
      const user = $firebaseAuth.currentUser
      if (!user) return null

      return await user.getIdToken(forceRefresh)
    } catch (err: any) {
      error.value = getFirebaseErrorMessage(err.code)
      return null
    }
  }

  /**
   * Initialize auth state listener
   */
  const initAuthStateListener = () => {
    if (process.client) {
      onAuthStateChanged($firebaseAuth, (user: FirebaseUser | null) => {
        if (user) {
          currentUser.value = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            emailVerified: user.emailVerified,
            phoneNumber: user.phoneNumber,
          }
        } else {
          currentUser.value = null
        }
      })
    }
  }

  /**
   * Convert Firebase error codes to user-friendly messages
   */
  const getFirebaseErrorMessage = (errorCode: string): string => {
    const errorMessages: Record<string, string> = {
      'auth/user-not-found': 'No user found with this email address.',
      'auth/wrong-password': 'Incorrect password.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/invalid-email': 'Invalid email address.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/network-request-failed': 'Network error. Please check your connection.',
      'auth/invalid-credential': 'Invalid email or password.',
      'auth/requires-recent-login': 'Please log in again to complete this action.',
    }

    return errorMessages[errorCode] || 'An error occurred during authentication.'
  }

  return {
    // State
    currentUser: readonly(currentUser),
    isLoading: readonly(isLoading),
    error: readonly(error),
    
    // Computed
    isAuthenticated,
    isEmailVerified,
    
    // Methods
    signIn,
    signUp,
    signOut: signOutUser,
    resetPassword,
    getIdToken,
    initAuthStateListener,
  }
}
